// settings.ts — app-level scenario defaults (Einstellungen page), persisted to
// localStorage under 'etf-cockpit-settings'. These are DEFAULTS ONLY: they seed a
// brand-new scenario (scenario.ts's createScenario) or an explicit "Auf Standard
// zurücksetzen" on an existing scenario slot. They never retroactively rewrite a
// scenario a user has already configured — see CockpitView's resetSlot, which is the
// only caller that touches an existing slot, and it always builds a fresh scenario.
//
// Unlike storage.ts's PersistedState, this key is unversioned by design: sanitizeSettings
// already merges field-by-field over today's factory defaults (not a raw JSON.parse
// passthrough), so an old stored blob missing a field a later release added — e.g.
// lumpSumEur (Einmalerlag) — simply backfills to that field's default instead of being
// discarded or crashing. That merge behaviour IS the migration; no version bump needed
// unless a field's *meaning* changes incompatibly (not just "a field got added").
import { DEFAULT_ACCENT_PRESET, isAccentPreset } from './theme.js';
import type { AccentPreset } from './theme.js';

const STORAGE_KEY = 'etf-cockpit-settings';

export interface AppSettings {
  /** Darstellung (Einstellungen): the accent-color theme preset, applied as CSS custom
   * properties on :root (see lib/theme.ts) and picked up by the charts' primary series.
   * Defaults to 'blau'. */
  accentPreset: AccentPreset;
  annualReturnPercent: number;
  incomeYieldPercent: number;
  terPercent: number;
  /** Einmalerlag (lump sum) default, in €, for a brand-new/reset scenario's Sparplan
   * section — same field a scenario already has (ForecastFormState/BacktestFormState.
   * lumpSumEur), just now house-configurable instead of a hardcoded 0. */
  lumpSumEur: number;
  monthlyEur: number;
  executionFeeEur: number;
  kestPercent: number;
  distributionFrequency: 'annual' | 'quarterly';
  /** Sparplan-Dynamik: annual automatic increase of the monthly rate, in percent
   * (0 = off, today's behaviour). Converted to contributionStepUpBp at the API boundary. */
  contributionStepUpPercent: number;
  /** Inflationsbereinigung (Feature 2, frontend-only real-terms display): default annual
   * inflation rate, in percent, that seeds the results view's session-only toggle. Never
   * sent to the API and never applied to the stored/nominal engine output — see
   * lib/realTerms.ts. */
  inflationPercent: number;
  /** "Erklärungen" (document-by-design toggle, visual-redesign spec): when true, the
   * teaching/convenience hint text scattered across the form (field usage hints, section
   * descriptions, chart-symbol legends, …) renders inline — see components/Hint.tsx.
   * OFF by default so the UI reads clean via labels/structure alone; flipping it back on
   * is one tap away (app-bar / drawer / sidebar ⓘ icon, or this same field's own switch in
   * Einstellungen -> Darstellung). Load-bearing honesty content (disclaimers, prefill
   * warnings, disabled-feature reasons, error/empty-state messages, …) is NEVER gated by
   * this — see Hint.tsx's header comment for the exact split. */
  showExplanations: boolean;
}

/** Same bounds as the scenario form fields these values seed (ForecastFields.tsx) —
 * duplicated here (not imported) so the settings form can validate independently of
 * any one scenario mode's field set. kestPercent's 0..100 mirrors the server's BP_RATE
 * schema bound (schemas.ts), which is the widest range the API will ever accept. */
export const SETTINGS_BOUNDS = {
  annualReturnPercent: { min: -10, max: 100 },
  incomeYieldPercent: { min: 0, max: 20 },
  terPercent: { min: 0, max: 2 },
  // Same bound as the per-scenario Einmalerlag field (ForecastFields.tsx/BacktestFields.tsx).
  lumpSumEur: { min: 0, max: 200000 },
  monthlyEur: { min: 0, max: 3000 },
  executionFeeEur: { min: 0, max: 20 },
  kestPercent: { min: 0, max: 100 },
  contributionStepUpPercent: { min: 0, max: 20 },
  inflationPercent: { min: 0, max: 20 },
} as const;

export function defaultSettings(): AppSettings {
  return {
    accentPreset: DEFAULT_ACCENT_PRESET,
    annualReturnPercent: 6,
    incomeYieldPercent: 1.3,
    terPercent: 0.2,
    // 0 — off by default, matches today's per-scenario default exactly until a user
    // opts into a house default lump sum here.
    lumpSumEur: 0,
    monthlyEur: 100,
    executionFeeEur: 1.5,
    kestPercent: 27.5,
    // Most Austrian-held distributing UCITS ETFs pay quarterly in practice; 'annual'
    // remains a one-tap change away and is still the engine's own fallback when a
    // ForecastConfig omits the field entirely (forecast.ts).
    distributionFrequency: 'quarterly',
    // Off by default — a brand-new scenario reproduces today's fixed-rate behaviour
    // exactly until the user opts in.
    contributionStepUpPercent: 0,
    // 2.0% — a common long-run Austrian/euro-area inflation assumption (ECB's own
    // target). Only seeds the results view's real-terms toggle default; the toggle
    // itself is OFF by default (see lib/realTerms.ts), so this has zero effect until a
    // user explicitly turns real-terms display on.
    inflationPercent: 2,
    // Off by default — "document by design, not text": a brand-new install shows the
    // clean, label-driven UI; teaching text is opt-in via the ⓘ toggle.
    showExplanations: false,
  };
}

function clampNumber(value: unknown, bounds: { min: number; max: number }, fallback: number): number {
  const n = typeof value === 'number' ? value : NaN;
  if (!Number.isFinite(n)) return fallback;
  return Math.min(bounds.max, Math.max(bounds.min, n));
}

/** Merges a possibly-partial/stale-shaped raw object over the current factory defaults,
 * clamping every numeric field to its valid range — never trusts localStorage content
 * blindly (a hand-edited or pre-migration value could otherwise reach the API). */
export function sanitizeSettings(raw: Partial<AppSettings> | null | undefined): AppSettings {
  const fallback = defaultSettings();
  if (!raw) return fallback;
  return {
    accentPreset: isAccentPreset(raw.accentPreset) ? raw.accentPreset : fallback.accentPreset,
    annualReturnPercent: clampNumber(raw.annualReturnPercent, SETTINGS_BOUNDS.annualReturnPercent, fallback.annualReturnPercent),
    incomeYieldPercent: clampNumber(raw.incomeYieldPercent, SETTINGS_BOUNDS.incomeYieldPercent, fallback.incomeYieldPercent),
    terPercent: clampNumber(raw.terPercent, SETTINGS_BOUNDS.terPercent, fallback.terPercent),
    lumpSumEur: clampNumber(raw.lumpSumEur, SETTINGS_BOUNDS.lumpSumEur, fallback.lumpSumEur),
    monthlyEur: clampNumber(raw.monthlyEur, SETTINGS_BOUNDS.monthlyEur, fallback.monthlyEur),
    executionFeeEur: clampNumber(raw.executionFeeEur, SETTINGS_BOUNDS.executionFeeEur, fallback.executionFeeEur),
    kestPercent: clampNumber(raw.kestPercent, SETTINGS_BOUNDS.kestPercent, fallback.kestPercent),
    distributionFrequency: raw.distributionFrequency === 'annual' || raw.distributionFrequency === 'quarterly'
      ? raw.distributionFrequency
      : fallback.distributionFrequency,
    contributionStepUpPercent: clampNumber(
      raw.contributionStepUpPercent,
      SETTINGS_BOUNDS.contributionStepUpPercent,
      fallback.contributionStepUpPercent,
    ),
    inflationPercent: clampNumber(raw.inflationPercent, SETTINGS_BOUNDS.inflationPercent, fallback.inflationPercent),
    showExplanations: typeof raw.showExplanations === 'boolean' ? raw.showExplanations : fallback.showExplanations,
  };
}

export function loadSettings(): AppSettings {
  if (typeof localStorage === 'undefined') return defaultSettings();
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return defaultSettings();
    return sanitizeSettings(JSON.parse(raw) as Partial<AppSettings>);
  } catch {
    return defaultSettings();
  }
}

export function saveSettings(settings: AppSettings): void {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
  } catch {
    // Storage full / disabled (private browsing) — silently drop, same as storage.ts.
  }
}
